/* eslint-disable react/no-unescaped-entities */
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import useNonInitialEffect from '@versiondos/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import SelectDaycareServices from './SelectDaycareServices';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { DaycareService, DaycareServiceWrapper } from 'model/Service';
import { fetchDaycareServicesThunk } from 'actions/services/ServicesActions';
import { useApiClientWrapper } from 'hooks';
import { ServicesStatus } from 'reducers/services/ServicesState';
import styles from './DaycareServiceSelector.module.scss';

interface Props {
    petId: number;
    selectedService?: DaycareServiceWrapper;
    readOnly?: boolean;
    withoutBorder?: boolean;
    servicesSelected?: Array<number>;
    multiplePets?: boolean;
    customizable?: boolean;
    onSelectService: (service: DaycareServiceWrapper, data: boolean) => void;
}

export const DaycareServiceSelector: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const ref = useRef<HTMLDivElement>(null);

    const daycareServices = useSelector<RootState, Array<DaycareService>>(
        state => state.services.daycareServices || []
    );

    const daycareServicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const services: Array<DaycareServiceWrapper> = daycareServices
        .map(service => ({
            ...service,
            durationService: {
                hours: Math.trunc(service.duration / 60),
                minutes: service.duration % 60
            },
            beforePrice: service.price,
            serviceColor: service.serviceColor,
            latePickupFee: service.latePickupFee,
            dropOffTime: service.dropOffTime,
            pickUpTime: service.pickUpTime,
            variableDuration: service.variableDuration
        }))
        .sort((wrapper, otherWrapper) =>
            wrapper.serviceName.toLowerCase().localeCompare(otherWrapper.serviceName.toLowerCase())
        );

    const [loading, setLoading] = useState(true);

    const getServicesUnused = () => {
        if (props.servicesSelected) {
            const servicesToSelect: Array<DaycareServiceWrapper> = [];
            services.forEach(serv => {
                const serviceFounded = props.servicesSelected?.find(s => s === serv?.serviceId);
                if (!serviceFounded) {
                    servicesToSelect.push(serv);
                }
            });
            return servicesToSelect;
        }
        return services;
    };

    useEffect(() => {
        setLoading(true);
        dispatch(fetchDaycareServicesThunk(props.petId, apiClientWrapper.marketplaceId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.petId, apiClientWrapper.marketplaceId]);

    useEffect(() => {
        if (!props.readOnly) {
            setTimeout(() => {
                ref?.current?.scrollIntoView();
            }, 2000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.petId]);

    useNonInitialEffect(() => {
        switch (daycareServicesStatus) {
            case ServicesStatus.FetchingDaycareServices:
                setLoading(true);
                break;
            case ServicesStatus.FetchDaycareServicesSuccess:
            case ServicesStatus.Error:
                setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [daycareServicesStatus]);

    const emptyServicesComponent = (
        <div id="booking_div_no_services_alert">
            <AlertNewBooking
                className={styles.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <span className={styles.alertText}>No services available for this pet's size or hair length</span>
            </AlertNewBooking>
        </div>
    );

    return (
        <div
            className={clsx(styles.container, {
                [styles.withoutBorder]: props.withoutBorder
            })}
        >
            {loading ? (
                <Spinner />
            ) : (
                <div ref={ref}>
                    <p className={styles.title}>Daycare Services</p>
                    {getServicesUnused()?.length === 0 ? (
                        emptyServicesComponent
                    ) : (
                        <SelectDaycareServices
                            customizable={props.customizable}
                            placeholder="Select a Service"
                            selectedOption={props.selectedService}
                            options={getServicesUnused()}
                            onSelect={(service: DaycareServiceWrapper, data: boolean) => {
                                props.onSelectService(service, data);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default DaycareServiceSelector;
